import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { OperationIcon } from '@/components/icons/OperationIcon';
import { getGuideBonusesData } from '@/lib/bonuses-client';
import {
  getAllowedPersonalityNames,
  getAllowedRaceNames,
  getOperationReference,
  getOperationReferences,
  operationKindLabel,
  type OperationKind,
  type OperationReference,
} from '@/lib/classic-operation-reference';
import type { ReactNode } from 'react';

export const Route = createFileRoute('/classic-guide/operations/$operationKind/$operationId')({
  component: ClassicOperationDetailPage,
});

function textWithBreaks(value: string) {
  const normalized = `${value || ''}`.replace(/<br\s*\/?>/gi, '\n');
  const lines = normalized.split('\n').map(line => line.trim()).filter(Boolean);
  if (!lines.length) {
    return 'No guide description is available for this operation yet.';
  }
  return lines.map((line, index) => (
    <span key={`${line}-${index}`}>
      {line}
      {index < lines.length - 1 ? <br /> : null}
    </span>
  ));
}

function isOperationKind(value: string): value is OperationKind {
  return value === 'attack' || value === 'magic' || value === 'thievery';
}

function Section({ children, title }: { children: ReactNode; title: string }) {
  return (
    <Card className="space-y-4 p-6">
      <h3 className="font-cinzel text-xl text-[#ffe58a]">{title}</h3>
      {children}
    </Card>
  );
}

function AtAGlance({ rows }: { rows: Array<[string, string]> }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label} className="rounded-lg border border-white/12 bg-[#050505] p-4">
          <p className="text-sm font-semibold uppercase tracking-wide text-[#d4c8b2]">{label}</p>
          <p className="mt-1 text-base font-semibold text-[#f6f1e8]">{value}</p>
        </div>
      ))}
    </div>
  );
}

function parentPage(operation: OperationReference) {
  if (operation.kind === 'attack') {
    return { pageId: 'combat', title: 'Combat' };
  }
  if (operation.kind === 'thievery') {
    return { pageId: 'thievery', title: 'Thievery' };
  }
  return { pageId: 'mystics', title: 'Mystics' };
}

function operationIconCode(operation: OperationReference): string {
  return operation.action?.imageCode || operation.name;
}

function atAGlanceRows(operation: OperationReference, allowedRaces: string[], allowedPersonalities: string[]): Array<[string, string]> {
  const rows: Array<[string, string]> = [
    ['Category', operationKindLabel(operation.kind)],
    ['Operation Type', operation.typeLabel],
    ['Availability', operation.availability],
  ];

  if (operation.kind === 'magic') {
    rows.push([
      'Duration',
      operation.duration > 0 ? 'Applies a lasting effect when successful' : 'Resolves immediately on cast'
    ]);
    if (operation.runeCost > 0) {
      rows.push(['Rune Cost', `${operation.runeCost}`]);
    }
  } else if (operation.kind === 'thievery') {
    rows.push(['Resolution', 'Consumes stealth and resolves immediately']);
  } else {
    rows.push(['Resolution', 'Commits armies and resolves as an attack']);
  }

  if (allowedRaces.length) {
    rows.push(['Race access', allowedRaces.join(', ')]);
  }

  if (allowedPersonalities.length) {
    rows.push(['Personality access', allowedPersonalities.join(', ')]);
  }

  return rows;
}

function operationRules(operation: OperationReference): string[] {
  if (operation.kind === 'attack') {
    return [
      'A valid target, available generals and enough modified offense are required.',
      'The army leaves home and returns later according to attack-time modifiers.',
      'Ceasefire, protection, missing generals, unpaid troops or province state may prevent the attack.',
    ];
  }

  if (operation.kind === 'thievery') {
    return [
      'Thieves, stealth and a valid target are required.',
      'The operation resolves at once and spends stealth.',
      'Relation level may matter for hostile or darker operations.',
    ];
  }

  const lines = [
    'You need mana, wizards and any required runes to cast.',
    'Self spells work on your own province. Intel and hostile spells require a valid target.',
  ];

  if (operation.duration > 0) {
    lines.push('A successful cast creates an effect that lasts for a time.');
  } else {
    lines.push('A successful cast resolves immediately.');
  }

  return lines;
}

function restrictionNotes(operation: OperationReference, allowedRaces: string[], allowedPersonalities: string[]): string[] {
  const notes: string[] = [];

  if (allowedRaces.length) {
    notes.push(`Only available to these races: ${allowedRaces.join(', ')}.`);
  }

  if (allowedPersonalities.length) {
    notes.push(`Only available to these personalities: ${allowedPersonalities.join(', ')}.`);
  }

  if (!notes.length) {
    if (operation.kind === 'attack') {
      notes.push('Target size, relation state, ceasefire and province readiness may prevent the attack.');
    } else if (operation.kind === 'thievery') {
      notes.push('Stealth, target legality and relation limits are the main restrictions.');
    } else {
      notes.push('Mana, runes, target legality and active defensive effects are the main restrictions.');
    }
  }

  return notes;
}

function playerFacingMessages(operation: OperationReference, data: Awaited<ReturnType<typeof getGuideBonusesData>>): Array<[string, string]> {
  const spellText = data.stringsPayload?.spellstr?.[`${operation.id}`] || [];
  return [
    ['What you see on success', `${spellText[1] || ''}`.trim()],
    ['What the target may see', `${spellText[2] || ''}`.trim()],
    ['Common description text', `${spellText[3] || ''}`.trim()],
  ].filter((row): row is [string, string] => Boolean(row[1]));
}

function relatedOperations(data: Awaited<ReturnType<typeof getGuideBonusesData>>, operation: OperationReference) {
  return getOperationReferences(data)
    .filter(candidate => candidate.kind === operation.kind && candidate.id !== operation.id)
    .slice(0, 6);
}

function ClassicOperationDetailPage() {
  const { operationId, operationKind } = Route.useParams();
  if (!isOperationKind(operationKind)) {
    throw notFound();
  }

  const parsedOperationId = Number(operationId);
  if (!Number.isFinite(parsedOperationId)) {
    throw notFound();
  }

  const guideDataQuery = useQuery({
    queryKey: ['classic-guide-generated-data'],
    queryFn: getGuideBonusesData,
    staleTime: 5 * 60 * 1000,
  });

  if (guideDataQuery.isLoading) {
    return (
      <Card className="p-6 text-base text-[#ffe58a]">
        Loading current age operation data...
      </Card>
    );
  }

  const data = guideDataQuery.data;
  const operation = data ? getOperationReference(data, parsedOperationId) : undefined;

  if (!data || !operation || operation.kind !== operationKind) {
    throw notFound();
  }

  const allowedRaces = getAllowedRaceNames(data, operation);
  const allowedPersonalities = getAllowedPersonalityNames(data, operation);
  const related = relatedOperations(data, operation);
  const parent = parentPage(operation);
  const messageRows = playerFacingMessages(operation, data);

  return (
    <div className="space-y-5">
      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-4 sm:grid-cols-[96px_1fr]">
            <OperationIcon
              code={operationIconCode(operation)}
              width={96}
              height={96}
              className="h-24 w-24 overflow-hidden rounded-xl border border-[#f0d060]/30"
            />
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#d4c8b2]">
                {operationKindLabel(operation.kind)} | {operation.typeLabel}
              </p>
              <h2 className="mt-2 font-cinzel text-3xl text-[#f6f1e8]">{operation.name}</h2>
              <p className="mt-3 max-w-4xl text-base leading-7 text-[#e2d8c4]">
                {textWithBreaks(operation.description)}
              </p>
            </div>
          </div>
          <Link
            to="/classic-guide/$pageId"
            params={{ pageId: parent.pageId }}
            className="rounded-lg border border-[#f0d060]/35 px-3 py-1.5 text-sm font-semibold text-[#ffe58a] hover:bg-[#f0d060]/12"
          >
            Back to {parent.title}
          </Link>
        </div>
      </Card>

      <Section title="At a Glance">
        <AtAGlance rows={atAGlanceRows(operation, allowedRaces, allowedPersonalities)} />
      </Section>

      <Section title="Rules">
        <ul className="list-disc space-y-1 pl-5 text-base leading-8 text-[#f6f1e8]">
          {operationRules(operation).map(line => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </Section>

      <Section title="Restrictions and Risks">
        <ul className="list-disc space-y-1 pl-5 text-base leading-8 text-[#f6f1e8]">
          {restrictionNotes(operation, allowedRaces, allowedPersonalities).map(line => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </Section>

      {messageRows.length ? (
        <Section title="Messages">
          <div className="space-y-2">
            {messageRows.map(([label, value]) => (
              <div key={label} className="rounded-lg border border-white/12 bg-[#050505] p-4">
                <p className="text-sm font-semibold uppercase tracking-wide text-[#d4c8b2]">{label}</p>
                <p className="mt-2 text-base leading-7 text-[#f6f1e8]">{textWithBreaks(value)}</p>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      <Section title="Related Operations">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {related.map(candidate => (
            <Link
              key={candidate.id}
              to="/classic-guide/operations/$operationKind/$operationId"
              params={{ operationId: `${candidate.id}`, operationKind: candidate.kind }}
            >
              <Card className="h-full p-4 transition-colors hover:border-[#f0d060]/45">
                <div className="flex gap-3">
                  <OperationIcon
                    code={operationIconCode(candidate)}
                    width={40}
                    height={40}
                    className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-[#f0d060]/25"
                  />
                  <div className="min-w-0">
                    <p className="text-sm text-[#ffe58a]">{candidate.typeLabel}</p>
                    <h3 className="mt-1 font-cinzel text-base text-[#f6f1e8]">{candidate.name}</h3>
                    <p className="mt-1 line-clamp-3 text-sm leading-6 text-[#e2d8c4]">{candidate.description}</p>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}
